
import { useState } from "react";
import { TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { GalleryCarousel } from "./carousel";
import { GalleryPhoto } from "./carousel/types";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import { Button } from "@/components/ui/button";
import { Crown, Image, LogIn } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface GalleryTabContentProps {
  value: string;
  photos: GalleryPhoto[];
  title?: string;
  onPhotoDeleted: () => void;
  isGuestMode?: boolean;
}

export function GalleryTabContent({
  value,
  photos,
  title,
  onPhotoDeleted,
  isGuestMode = false
}: GalleryTabContentProps) {
  const { language } = useLanguage();
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');

  // Force refresh photos on sort order change
  const handleSortChange = (value: 'newest' | 'oldest') => {
    setSortOrder(value);
    // Force a refresh by triggering the onPhotoDeleted callback
    // This is a bit of a hack, but it works to refresh the display
    setTimeout(() => {
      onPhotoDeleted();
    }, 10);
  };

  // Guest mode display with placeholder
  if (isGuestMode) {
    // Create placeholder photos (same image in different slots)
    const placeholderPhotos: GalleryPhoto[] = Array(3).fill({
      id: `placeholder-${value}`,
      url: "https://images.unsplash.com/photo-1613550758035-c7c0da735ba6?w=800&auto=format&fit=crop",
      title: t('gallery.yourBabyPhotosHere', language) || "Your baby's photos will appear here",
      created_at: new Date().toISOString(),
    });
    
    return (
      <TabsContent value={value} className="py-2">
        <>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">
              {title || t(`gallery.${value}Photos`, language)}
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {t('gallery.sortBy', language)}:
              </span>
              <Select value={sortOrder} onValueChange={(value: 'newest' | 'oldest') => setSortOrder(value)}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue placeholder={t('gallery.newest', language)} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">{t('gallery.newest', language)}</SelectItem>
                  <SelectItem value="oldest">{t('gallery.oldest', language)}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {placeholderPhotos.map((_, index) => (
              <Card key={`placeholder-${index}`} className="overflow-hidden">
                <CardContent className="p-0">
                  <div className="aspect-square relative bg-muted flex flex-col items-center justify-center p-4 text-center">
                    <Image className="h-16 w-16 mb-4 text-muted-foreground" />
                    <h3 className="text-lg font-medium mb-2">
                      {t('gallery.yourBabyPhotosHere', language) || "Your baby's photos will appear here"}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {t('gallery.uploadAfterSignup', language) || "Upload your own photos after signing up"}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-4 bg-muted/30 p-4 rounded-lg border border-muted">
            <h4 className="font-medium mb-2">{t('gallery.premiumFeature', language) || 'Premium Feature'}</h4>
            <p className="text-muted-foreground mb-3">{t('gallery.signInToViewPhotos', language) || 'Sign in to upload and manage your own photos'}</p>
            <div className="flex gap-2 justify-center">
              <Link to="/login">
                <Button size="sm" variant="outline">
                  <LogIn className="h-3 w-3 mr-1" />
                  {t('common.login', language)}
                </Button>
              </Link>
              <Link to="/premium">
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600">
                  <Crown className="h-3 w-3 mr-1" />
                  {t('index.premiumBenefits.upgradeToPremium', language)}
                </Button>
              </Link>
            </div>
          </div>
        </>
      </TabsContent>
    );
  }

  // Full gallery display for authenticated users
  return (
    <TabsContent value={value} className="py-2">
      {photos.length > 0 ? (
        <>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">
              {title || t(`gallery.${value}Photos`, language)}
              <span className="text-sm text-muted-foreground font-normal ml-2">
                ({photos.length} {photos.length === 1 ? t('gallery.photo', language) || 'photo' : t('gallery.photos', language) || 'photos'})
              </span>
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {t('gallery.sortBy', language)}:
              </span>
              <Select value={sortOrder} onValueChange={handleSortChange}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue placeholder={t('gallery.newest', language)} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">{t('gallery.newest', language)}</SelectItem>
                  <SelectItem value="oldest">{t('gallery.oldest', language)}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <GalleryCarousel 
            photos={photos} 
            onPhotoDeleted={onPhotoDeleted} 
            sortOrder={sortOrder}
          />
        </>
      ) : (
        <Card className="border-dashed">
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">
              {t('gallery.noPhotos', language) || 'No photos yet'}
            </p>
          </CardContent>
        </Card>
      )}
    </TabsContent>
  );
}
